import { useState, useEffect } from "react";
import { cleanBaselineHtml } from "../../../util/lexical";
import attachmentStorage from "../../../util/attachmentStorage";

/**
 * useAttachmentPersistence Hook
 * 
 * Manages attachment form persistence and draft saving
 * Handles both localStorage (add mode) and IndexedDB (edit mode)
 * 
 * @param {Object} params
 * @param {string} params.mode - "add" or "edit"
 * @param {string} params.id - Attachment ID (for edit mode)
 * @param {Object} params.attachmentData - Fetched attachment data
 * 
 * @returns {Object} Form state and persistence methods
 */
const useAttachmentPersistence = ({
  mode,
  id,
  attachmentData,
}) => {
  // Form state
  const [formData, setFormData] = useState({
    shortDescription: "",
  });
  
  const [files, setFiles] = useState([]);
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Initialize form data based on mode
  useEffect(() => {
    if (mode === "add") {
      // Add mode: Try to restore from localStorage
      // Use form-specific key to avoid cross-contamination with other forms (e.g., canvas)
      try {
        // Clear any leftover generic short_description from other forms
        localStorage.removeItem("short_description");
        
        const savedDesc = localStorage.getItem("attachment_short_description");
        if (savedDesc && savedDesc !== "undefined") {
          setFormData(prev => ({ ...prev, shortDescription: cleanBaselineHtml(savedDesc) }));
        }

        const savedAttachments = localStorage.getItem("attachments");
        if (savedAttachments && savedAttachments !== "undefined") {
          const parsed = JSON.parse(savedAttachments);
          const restoredFiles = Array.isArray(parsed)
            ? parsed.map((f) => ({
                name: f.name,
                type: f.type || "application/octet-stream",
                base64Data: f.base64 || f.attachment || "",
                attachment: f.base64 || f.attachment || "",
              }))
            : [];
          
          if (restoredFiles.length > 0) {
            setFiles(restoredFiles);
            setHasUnsavedChanges(true);
          }
          
          // Clear after successful restore
          localStorage.removeItem("attachments");
        }
      } catch (err) {
        console.error("Error restoring add-mode attachment draft:", err);
      }
    } else if (mode === "edit" && attachmentData) {
      // Edit mode: Initialize from fetched data
      
      // Process short description to handle both plain text and HTML content
      let shortDesc = attachmentData?.detail?.short_description || "";
      if (shortDesc && !shortDesc.includes('<div class="editor-shell">')) {
        // If it's plain text (no editor-shell wrapper), convert it to HTML format
        shortDesc = `<p>${shortDesc}</p>`;
      }
      
      setFormData(prev => ({
        ...prev,
        shortDescription: cleanBaselineHtml(shortDesc),
      }));

      // Build existing files list
      const existing = Array.isArray(attachmentData?.detail?.attachments)

        ? attachmentData?.detail?.attachments.map((att) => {
            return {
              name: (att.attachment || "").split("/").pop(),
              url: att.attachment,
              isExisting: true,
              attachment_id: att?.attachment_id,
            };
          })
        : [];

      setFiles(existing);
      setDataLoaded(true);
    }
  }, [mode, attachmentData]);

  // Auto-save for edit mode
  useEffect(() => {
    if (mode === "edit" && id && dataLoaded && hasUnsavedChanges) {
      const timeoutId = setTimeout(async () => {
        try {
          await attachmentStorage.storeAttachments(
            id,
            files,
            formData.shortDescription,
            removedAttachmentIds
          );
        } catch (err) {
          console.error("Auto-save (edit) to IndexedDB failed:", err);
        }
      }, 700);
      
      return () => clearTimeout(timeoutId);
    }
  }, [mode, id, dataLoaded, hasUnsavedChanges, files, formData.shortDescription, removedAttachmentIds]);

  // Save for preview (both modes)
  const saveForPreview = async () => {
    if (mode === "add") {
      // Add mode: Save to localStorage (with quota error handling)
      try {
        const base64Files = await Promise.all(
          files.map(async (file) => {
            if (file.isExisting) {
              return {
                name: file.name,
                type: "application/octet-stream",
                attachment: file.url,
                isExisting: true,
                attachment_id: file.attachment_id,
              };
            }

            if (file.base64Data) {
              return {
                name: file.name,
                type: file.type || "application/octet-stream",
                base64: file.base64Data,
                attachment: file.base64Data,
              };
            }

            if (file instanceof File) {
              return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                  resolve({
                    name: file.name,
                    type: file.type,
                    base64: reader.result,
                    attachment: reader.result,
                  });
                };
                reader.onerror = reject;
                reader.readAsDataURL(file);
              });
            }

            return file;
          })
        );

        // Try to save to localStorage with quota error handling
        try {
          localStorage.setItem("attachments", JSON.stringify(base64Files));
          localStorage.setItem("attachment_short_description", formData.shortDescription);
        } catch (quotaError) {
          if (quotaError.name === "QuotaExceededError") {
            // Clear potentially large data
            localStorage.removeItem("attachments");
            // Still save description if possible
            try {
              localStorage.setItem("attachment_short_description", formData.shortDescription);
            } catch (e) {
            }
          } else {
            throw quotaError;
          }
        }
      } catch (error) {
        console.error("Error in saveForPreview (add mode):", error);
        throw error;
      }
    } else {
      // Edit mode: Save to IndexedDB (no quota issues)
      await attachmentStorage.storeAttachments(
        id,
        files,
        formData.shortDescription,
        removedAttachmentIds
      );
      localStorage.setItem("editAttachmentReturningFromPreview", "true");
    }
  };

  // Clear stored data
  const clearStoredData = async () => {
    try {
      if (mode === "add") {
        localStorage.removeItem("attachments");
        localStorage.removeItem("attachment_short_description");
      } else {
        await attachmentStorage.deleteAttachments(id);
        localStorage.removeItem(`removedAttachmentIds_${id}`);
      }
    } catch (error) {
      console.error("Error clearing stored data:", error);
    }
  };

  return {
    formData,
    setFormData,
    files,
    setFiles,
    removedAttachmentIds,
    setRemovedAttachmentIds,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    saveForPreview,
    clearStoredData,
  };
};

export default useAttachmentPersistence;

